import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { trainingSessionsAPI, crewsAPI, usersAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../components/notifications/NotificationProvider';
import { useDashboardRefetch } from '../hooks/useDashboard';
import { formatDisplayDate } from '../utils/timeUtils';
import { canModifyContent, isCoach, getUserRole } from '../utils/roles';
import { applySessionFilters } from '../utils/sessionFilters';
import { NoAccessibleData } from '../components/PermissionDenied';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { OfflinePageWrapper, OfflineNoDataFallback } from '../components/OfflinePageWrapper';
import { useOfflineSafeFunctions } from '../hooks/useOfflineMode';

// Import new components
import TrainingSessionsHeader from '../components/training_sessions/TrainingSessionsHeader';
import TrainingSessionsStats from '../components/training_sessions/TrainingSessionsStats';
import TrainingSessionsTable from '../components/training_sessions/TrainingSessionsTable';
import MobileSessionCard from '../components/training_sessions/MobileSessionCard';
import EmptyState from '../components/training_sessions/EmptyState';
import CreateSessionModal from '../components/training_sessions/CreateSessionModal';
import ScheduleSessionModal from '../components/training_sessions/ScheduleSessionModal';
import SessionDetailsModal from '../components/training_sessions/SessionDetailsModal';
import ImportSessionsModal from '../components/training_sessions/ImportSessionsModal';
import TrainingSessionsFilters from '../components/training_sessions/TrainingSessionsFilters';
import QuickScheduleModal from '../components/training_sessions/QuickScheduleModal';

const TrainingSessions = () => {
  const { user } = useAuth();
  const { showConfirm, showSuccess, showError } = useNotifications();
  const { wrapFunction } = useOfflineSafeFunctions(showError);
  const queryClient = useQueryClient();
  const { invalidateDashboard } = useDashboardRefetch();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showQuickScheduleModal, setShowQuickScheduleModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [schedulingSession, setSchedulingSession] = useState(null);
  const [viewingSession, setViewingSession] = useState(null);
  const [quickSchedulingSession, setQuickSchedulingSession] = useState(null);
  const [filters, setFilters] = useState({
    crewId: null,
    trainingGroupId: null,
    userParticipation: null,
    dateRange: 'upcoming',
  });
  const [expandedSessions, setExpandedSessions] = useState(new Set()); // Track expanded parent sessions
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    session_type: 'unscheduled',
    duration: '90', // Duration in minutes - required for Stage 1
    training_type: 'water',
    location: '',
    crew_assignments: [],
    start_time: '', // Add start_time for child sessions
    selected_coach_ids: [],
  });

  const { data: sessions = [], isLoading } = useQuery(
    ['trainingSessions'],
    () => trainingSessionsAPI.getSessions({}),
    {
      staleTime: 30 * 1000,
    }
  );

  const { data: crews = [] } = useQuery('crews', () => crewsAPI.getCrews(), {
    staleTime: 5 * 60 * 1000,
  });

  // For loading coaches - assuming we need this for coach assignments
  const { data: coaches = [] } = useQuery('coaches', () => usersAPI.getUsers(), {
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      data.filter(
        (user) =>
          user.is_admin ||
          user.training_group_memberships?.some(
            (membership) => membership.role_in_group === 'coach'
          )
      ),
  });

  const createSessionMutation = useMutation(
    (sessionData) => trainingSessionsAPI.createSession(sessionData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('trainingSessions');
        invalidateDashboard(); // Also invalidate dashboard
        setShowCreateModal(false);
        resetForm();
        showSuccess('Training session created successfully');
      },
      onError: (error) => {
        showError('Failed to create training session. Please try again.');
        console.error('Create session error:', error);
      },
    }
  );

  const updateSessionMutation = useMutation(
    ({ id, data }) => trainingSessionsAPI.updateSession(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('trainingSessions');
        invalidateDashboard(); // Also invalidate dashboard
        setShowCreateModal(false);
        setEditingSession(null);
        resetForm();
        showSuccess('Training session updated successfully');
      },
      onError: (error) => {
        showError('Failed to update training session. Please try again.');
        console.error('Update session error:', error);
      },
    }
  );

  const scheduleSessionMutation = useMutation(
    ({ id, data }) => trainingSessionsAPI.scheduleSession(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('trainingSessions');
        invalidateDashboard(); // Also invalidate dashboard
        setShowScheduleModal(false);
        setSchedulingSession(null);
        showSuccess('Session scheduled successfully');
      },
      onError: (error) => {
        showError('Failed to schedule session. Please try again.');
        console.error('Schedule session error:', error);
      },
    }
  );

  const deleteSessionMutation = useMutation((id) => trainingSessionsAPI.deleteSession(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('trainingSessions');
      invalidateDashboard(); // Also invalidate dashboard
      showSuccess('Training session deleted successfully');
    },
    onError: (error) => {
      showError('Failed to delete training session. Please try again.');
      console.error('Delete session error:', error);
    },
  });

  const rescheduleSessionMutation = useMutation((id) => trainingSessionsAPI.rescheduleSession(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('trainingSessions');
      invalidateDashboard(); // Also invalidate dashboard
      showSuccess('Session rescheduled successfully');
    },
    onError: (error) => {
      showError('Failed to reschedule session. Please try again.');
      console.error('Reschedule session error:', error);
    },
  });

  const deleteSessionGroupMutation = useMutation(
    ({ id, deleteEntireGroup }) => trainingSessionsAPI.deleteSessionOrGroup(id, deleteEntireGroup),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('trainingSessions');
        invalidateDashboard(); // Also invalidate dashboard
        showSuccess('Session group deleted successfully');
      },
      onError: (error) => {
        showError('Failed to delete session group. Please try again.');
        console.error('Delete session group error:', error);
      },
    }
  );

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      session_type: 'unscheduled',
      duration: '90',
      training_type: 'water',
      location: '',
      crew_assignments: [],
      start_time: '',
      selected_coach_ids: [],
    });
  };

  const addCrewFromDropdown = (crewId) => {
    if (
      crewId &&
      !formData.crew_assignments.some((assignment) => assignment.crew_id === crewId.toString())
    ) {
      setFormData({
        ...formData,
        crew_assignments: [...formData.crew_assignments, { crew_id: crewId.toString() }],
      });
    }
  };

  const removeCrewAssignment = (index) => {
    const newAssignments = formData.crew_assignments.filter((_, i) => i !== index);
    setFormData({ ...formData, crew_assignments: newAssignments });
  };

  const updateCrewAssignment = (index, field, value) => {
    const newAssignments = [...formData.crew_assignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };
    setFormData({ ...formData, crew_assignments: newAssignments });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isChildSession = editingSession && !!editingSession.parent_session_id;

    const sessionData = {
      title: formData.title,
      description: formData.description,
      date: formData.date, // Always include date - it's required
      session_type: formData.session_type,
      duration: formData.duration ? parseInt(formData.duration) : 90,
      location: formData.location,
      equipment_id: null, // Always null for Stage 1
      crew_assignments: formData.crew_assignments.filter((assignment) => assignment.crew_id),
    };

    // For child sessions, include start time and replace coach assignments on save
    if (isChildSession && editingSession) {
      const t = formData.start_time;
      sessionData.default_start_time =
        t && t.split(':').length === 2 ? `${t}:00` : t;
      sessionData.coach_assignments = (formData.selected_coach_ids || []).map((id) => ({
        coach_id: Number(id),
        role: 'coach',
      }));
    }

    if (editingSession) {
      // For parent sessions, we need to update all child sessions with the same title/description
      if (
        !isChildSession &&
        editingSession.childSessions &&
        editingSession.childSessions.length > 0
      ) {
        const parentUpdateData = {
          title: formData.title,
          description: formData.description,
          date: formData.date,
          duration: formData.duration ? parseInt(formData.duration) : 90,
          location: formData.location,
          crew_assignments: formData.crew_assignments.filter((assignment) => assignment.crew_id), // Include crew assignments
          updateChildren: true, // Flag to indicate child sessions should be updated too
        };
        updateSessionMutation.mutate({ id: editingSession.id, data: parentUpdateData });
      } else {
        updateSessionMutation.mutate({ id: editingSession.id, data: sessionData });
      }
    } else {
      createSessionMutation.mutate(sessionData);
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    const isChildSession = !!session.parent_session_id;

    setFormData({
      title: session.title || '',
      description: session.description || '',
      date: session.date ? session.date.split('T')[0] : '',
      session_type: session.session_type || 'unscheduled',
      duration: session.duration?.toString() || '90',
      training_type: session.training_type || 'water',
      location: session.location || '',
      crew_assignments: session.crew_assignments || [],
      start_time: isChildSession
        ? (typeof session.default_start_time === 'string'
            ? session.default_start_time.slice(0, 5)
            : '') || ''
        : '',
      selected_coach_ids: isChildSession
        ? (session.user_assignments || [])
            .filter((a) => (a.role || '').toLowerCase() === 'coach')
            .map((a) => a.user_id)
        : [],
    });
    setShowCreateModal(true);
  };

  const handleSchedule = (session) => {
    // For better user experience, navigate directly to the Planner page
    // Extract crew IDs from the session's crew assignments
    const crewIds = session.crew_assignments?.map((assignment) => assignment.crew_id) || [];

    // Navigate to the Planner page with the session date and crew selections
    // Ensure we only use the date part (YYYY-MM-DD) without time
    const sessionDate = session.date ? session.date.split('T')[0] : null;
    const searchParams = new URLSearchParams();

    if (sessionDate) {
      searchParams.set('date', sessionDate);
    }

    if (crewIds.length > 0) {
      searchParams.set('crews', crewIds.join(','));
    }

    navigate(`/planner?${searchParams.toString()}`);
  };

  const handleViewDetails = (session) => {
    setViewingSession(session);
    setShowDetailsModal(true);
  };

  const handleQuickSchedule = (session) => {
    setQuickSchedulingSession(session);
    setShowQuickScheduleModal(true);
  };

  const handleDelete = async (session) => {
    const hasGroup = session.is_original;

    if (hasGroup) {
      const confirmed = await showConfirm(
        'This session has split instances. Do you want to delete the entire session group?',
        'Delete Session Group'
      );

      if (confirmed) {
        const deleteAll = await showConfirm(
          'Delete all sessions in this group?',
          'Confirm Deletion'
        );

        deleteSessionGroupMutation.mutate({
          id: session.id,
          deleteEntireGroup: deleteAll,
        });
      }
    } else {
      const confirmed = await showConfirm(
        'Are you sure you want to delete this training session?',
        'Delete Session'
      );

      if (confirmed) {
        deleteSessionMutation.mutate(session.id);
      }
    }
  };

  const handleReschedule = async (session) => {
    const confirmed = await showConfirm(
      'Are you sure you want to reschedule this session back to unscheduled?',
      'Reschedule Session'
    );

    if (confirmed) {
      rescheduleSessionMutation.mutate(session.id);
    }
  };

  const formatSessionTiming = (session) => {
    if (session.session_type === 'scheduled') {
      const startTime = session.default_start_time?.slice(0, 5) || '';
      const endTime = calculateEndTime(session.default_start_time, session.duration);
      return `${startTime} - ${endTime}`;
    }
    return `${session.duration || 90} minutes`;
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';

    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + duration * 60000);
    return endDate.toTimeString().slice(0, 5);
  };

  const getSessionStatus = (session) => {
    // For child sessions, show scheduled status
    if (session.parent_session_id) {
      return session.session_type === 'scheduled' ? 'scheduled' : 'unscheduled';
    }

    // For parent sessions
    if (!session.childSessions || session.childSessions.length === 0) {
      return 'unscheduled';
    }

    // Check if all crews have been scheduled
    // For parent sessions, check if any crews are left unscheduled
    const remainingCrews = session.crew_assignments ? session.crew_assignments.length : 0;
    const hasScheduledChildren = session.childSessions.length > 0;

    if (remainingCrews === 0 && hasScheduledChildren) {
      return 'completed';
    }

    return remainingCrews > 0 ? 'partially-scheduled' : 'unscheduled';
  };

  const formatCrewNames = (crewAssignments, session = null) => {
    if (!crewAssignments || crewAssignments.length === 0) {
      // Check if this is a parent session with all crews scheduled
      if (session && session.childSessions && session.childSessions.length > 0) {
        const totalScheduledCrews = session.childSessions.reduce((total, child) => {
          return total + (child.crew_assignments ? child.crew_assignments.length : 0);
        }, 0);

        if (totalScheduledCrews > 0) {
          return 'All crews scheduled';
        }
      }
      return 'Open Session';
    }
    return crewAssignments
      .map(
        (assignment) => crews.find((crew) => crew.id === assignment.crew_id)?.name || 'Unknown Crew'
      )
      .join(', ');
  };

  // Check if user has coach role in any training group
  const userIsCoach = isCoach(user);

  const canModify = canModifyContent(user);

  // Helper functions for hierarchical session view
  const organizeSessionsHierarchically = (allSessions) => {
    const parentSessions = [];
    const childSessionsMap = new Map();

    // First pass: check if a child session has a parent session present in the list
    allSessions.forEach((session) => {
      const parentSessionPresent = allSessions.some((s) => s.id === session.parent_session_id);
      session.parent_session_present = parentSessionPresent;
    });

    // Second pass: identify parent sessions and group children
    allSessions.forEach((session) => {
      if (
        !session.parent_session_id ||
        (!session.parent_session_present && session.session_type === 'scheduled')
      ) {
        // This is either a standalone session or a parent session
        parentSessions.push(session);
      } else {
        // This is a child session
        const parentId = session.parent_session_id;
        if (!childSessionsMap.has(parentId)) {
          childSessionsMap.set(parentId, []);
        }
        childSessionsMap.get(parentId).push(session);
      }
    });

    // Third pass: attach children to parents
    return parentSessions.map((parent) => ({
      ...parent,
      childSessions: childSessionsMap.get(parent.id) || [],
    }));
  };

  const toggleSessionExpansion = (sessionId) => {
    setExpandedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const expandAllSessions = () => {
    const allParentIds = hierarchicalSessions
      .filter((session) => session.childSessions && session.childSessions.length > 0)
      .map((session) => session.id);
    setExpandedSessions(new Set(allParentIds));
  };

  const collapseAllSessions = () => {
    setExpandedSessions(new Set());
  };

  // Apply filters to sessions
  const filteredSessions = applySessionFilters(sessions, filters, user);
  const hierarchicalSessions = organizeSessionsHierarchically(filteredSessions);

  // Filter handling functions
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      crewId: null,
      trainingGroupId: null,
      userParticipation: null,
      dateRange: 'upcoming',
    });
  };

  // Wrap handlers with offline protection
  const handleCreateClick = wrapFunction(() => {
    resetForm();
    setEditingSession(null);
    setShowCreateModal(true);
  }, 'create');

  const handleImportClick = wrapFunction(() => {
    setShowImportModal(true);
  }, 'import');

  if (isLoading) {
    return <LoadingSpinner text="Loading training sessions..." />;
  }

  const offlineFallback = (
    <OfflineNoDataFallback dataTypes={['training sessions']} className="mt-8" />
  );

  return (
    <OfflinePageWrapper requiredData={['sessions']} fallbackContent={offlineFallback}>
      <div className="space-y-6">
        <TrainingSessionsHeader
          canModify={canModify}
          handleCreate={handleCreateClick}
          handleImport={handleImportClick}
        />

        <TrainingSessionsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
        />

        <div className="card">
          {filteredSessions.length > 0 ? (
            <>
              <TrainingSessionsStats
                totalSessions={filteredSessions.length}
                parentSessionsCount={
                  hierarchicalSessions.filter((s) => s.childSessions && s.childSessions.length > 0)
                    .length
                }
                splitSessionsCount={filteredSessions.filter((s) => s.parent_session_id).length}
                onExpandAll={expandAllSessions}
                onCollapseAll={collapseAllSessions}
              />

              <div className="hidden overflow-x-auto md:block">
                <TrainingSessionsTable
                  sessions={hierarchicalSessions}
                  expandedSessions={expandedSessions}
                  onToggleExpand={toggleSessionExpansion}
                  onSchedule={handleSchedule}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onViewDetails={handleViewDetails}
                  onReschedule={handleReschedule}
                  onQuickSchedule={handleQuickSchedule}
                  getSessionStatus={getSessionStatus}
                  formatSessionTiming={formatSessionTiming}
                  formatCrewNames={formatCrewNames}
                  canModify={canModify}
                />
              </div>

              <div className="md:hidden">
                {hierarchicalSessions.map((session) => (
                  <MobileSessionCard
                    key={session.id}
                    session={session}
                    crews={crews}
                    canModify={canModify}
                    onToggleExpand={toggleSessionExpansion}
                    isExpanded={expandedSessions.has(session.id)}
                    handleEdit={handleEdit}
                    handleSchedule={handleSchedule}
                    handleDelete={handleDelete}
                    handleViewDetails={handleViewDetails}
                    handleReschedule={handleReschedule}
                    handleQuickSchedule={handleQuickSchedule}
                    formatCrewNames={formatCrewNames}
                    formatSessionTiming={formatSessionTiming}
                    getSessionStatus={getSessionStatus}
                  />
                ))}
              </div>
            </>
          ) : // Show RBAC-aware empty state based on user role
          canModify ? (
            <EmptyState
              canModify={canModify}
              handleCreate={handleCreateClick}
              handleImport={handleImportClick}
            />
          ) : (
            <NoAccessibleData dataType="training-sessions" userRole={getUserRole(user)} />
          )}
        </div>

        {showCreateModal && (
          <CreateSessionModal
            show={showCreateModal}
            handleClose={() => setShowCreateModal(false)}
            handleSubmit={handleSubmit}
            editingSession={editingSession}
            formData={formData}
            setFormData={setFormData}
            crews={crews}
            coaches={coaches}
            createSessionMutation={createSessionMutation}
            updateSessionMutation={updateSessionMutation}
            calculateEndTime={calculateEndTime}
            formatCrewNames={formatCrewNames}
            updateCrewAssignment={updateCrewAssignment}
            removeCrewAssignment={removeCrewAssignment}
            addCrewAssignment={addCrewFromDropdown}
          />
        )}

        {showScheduleModal && schedulingSession && (
          <ScheduleSessionModal
            isOpen={showScheduleModal}
            onClose={() => setShowScheduleModal(false)}
            session={schedulingSession}
            crews={crews}
            coaches={coaches}
            onSchedule={scheduleSessionMutation.mutate}
          />
        )}

        {showDetailsModal && viewingSession && (
          <SessionDetailsModal
            show={showDetailsModal}
            handleClose={() => setShowDetailsModal(false)}
            viewingSession={viewingSession}
            crews={crews}
            formatSessionTiming={formatSessionTiming}
            formatDisplayDate={formatDisplayDate}
          />
        )}

        {showImportModal && (
          <ImportSessionsModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />
        )}

        {showQuickScheduleModal && quickSchedulingSession && (
          <QuickScheduleModal
            show={showQuickScheduleModal}
            onClose={() => {
              setShowQuickScheduleModal(false);
              setQuickSchedulingSession(null);
            }}
            session={quickSchedulingSession}
            crews={crews}
            onSuccess={() => {
              queryClient.invalidateQueries('trainingSessions');
              invalidateDashboard();
            }}
          />
        )}
      </div>
    </OfflinePageWrapper>
  );
};

export default TrainingSessions;
